import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  LogOut,
  MonitorSmartphone,
  User as UserIcon,
} from "lucide-react";

import { authApi, type MeResponse, type Role } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { cn } from "@/lib/utils";
import { visibleSections } from "@/lib/menu";
import { Logo } from "@/components/Logo";

interface SidebarProps {
  role: Role | undefined;
  collapsed: boolean;
  onToggle: () => void;
  me: MeResponse;
  activeTenantId: string | null;
  onTenantChange: (tenantId: string) => void;
}

const SECTION_OPEN_KEY_PREFIX = "kgu-sidebar-section-open:";

export function Sidebar({
  role,
  collapsed,
  onToggle,
  me,
  activeTenantId,
  onTenantChange,
}: SidebarProps) {
  const { t } = useTranslation();
  const sections = visibleSections(role);

  // Стан розкриття для **кожної** collapsible-секції (Адміністрування, Інше, …),
  // ключований за titleKey. Кожен переживає reload.
  const [openByKey, setOpenByKey] = useState<Record<string, boolean>>(() => {
    const out: Record<string, boolean> = {};
    for (const s of sections) {
      if (s.collapsible && s.titleKey) {
        out[s.titleKey] = localStorage.getItem(SECTION_OPEN_KEY_PREFIX + s.titleKey) === "1";
      }
    }
    return out;
  });
  const toggleSection = (key: string) => {
    setOpenByKey((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(SECTION_OPEN_KEY_PREFIX + key, next[key] ? "1" : "0");
      return next;
    });
  };

  return (
    <aside
      className={cn(
        "h-screen sticky top-0 flex flex-col border-r border-slate-200 bg-white",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-3 border-b border-slate-200">
        {collapsed ? (
          <Logo variant="mark" height={32} className="mx-auto" />
        ) : (
          <div className="flex items-center gap-2">
            <Logo variant="mark" height={32} />
            <Logo variant="wordmark" height={26} />
          </div>
        )}
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto py-2">
        {sections.map((section, idx) => {
          if (section.collapsible && section.titleKey) {
            const key = section.titleKey;
            return (
              <CollapsibleSection
                key={idx}
                titleKey={key}
                collapsed={collapsed}
                isOpen={!!openByKey[key]}
                onToggle={() => toggleSection(key)}
              >
                <SectionItems items={section.items} collapsed={collapsed} />
              </CollapsibleSection>
            );
          }
          return (
            <div key={idx} className="mb-2">
              {section.titleKey && !collapsed && (
                <div className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider text-slate-400">
                  {t(section.titleKey)}
                </div>
              )}
              {section.titleKey && collapsed && idx > 0 && (
                <div className="border-t border-slate-200 mx-2 my-2" />
              )}
              <SectionItems items={section.items} collapsed={collapsed} />
            </div>
          );
        })}
      </nav>

      {/* Bottom: tenant / language / user */}
      <BottomActions
        collapsed={collapsed}
        me={me}
        activeTenantId={activeTenantId}
        onTenantChange={onTenantChange}
      />

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={onToggle}
        aria-label="Toggle sidebar"
        className="h-10 border-t border-slate-200 flex items-center justify-center
                   text-slate-500 hover:bg-slate-50 hover:text-slate-700"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
    </aside>
  );
}

// ───────────────────────────── Section items ─────────────────────────────

function SectionItems({
  items,
  collapsed,
}: {
  items: ReturnType<typeof visibleSections>[number]["items"];
  collapsed: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ul>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li key={item.path}>
            <NavLink
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 mx-2 my-0.5 py-2 rounded-md text-sm",
                  "transition-colors",
                  collapsed ? "justify-center" : "",
                  isActive
                    ? "bg-brand-navy-50 text-brand-navy-700 font-medium"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                )
              }
              title={collapsed ? t(item.i18nKey) : undefined}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{t(item.i18nKey)}</span>}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

// ──────────────────────────── Collapsible group ──────────────────────────

function CollapsibleSection({
  titleKey,
  collapsed,
  isOpen,
  onToggle,
  children,
}: {
  titleKey: string;
  collapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  if (collapsed) {
    // У згорнутому (icon) режимі collapsible-секції (Інше, Адміністрування)
    // ховаємо повністю — щоб icon-меню не скролилось. Доступ до них — після
    // розгортання sidebar'а.
    return null;
  }
  return (
    <div className="mb-2">
      <div className="mx-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center w-full px-3 my-0.5 py-2 rounded-md text-sm
                     text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        >
          <span className="flex-1 text-left text-xs uppercase tracking-wider text-slate-500">
            {t(titleKey)}
          </span>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          )}
        </button>
      </div>
      {isOpen && children}
    </div>
  );
}

// ──────────────────────────── Bottom actions ─────────────────────────────

function BottomActions({
  collapsed,
  me,
  activeTenantId,
  onTenantChange,
}: {
  collapsed: boolean;
  me: MeResponse;
  activeTenantId: string | null;
  onTenantChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.clear);

  const activeTenant = me.tenants.find((tt) => tt.id === activeTenantId);

  const logoutAll = useMutation({
    mutationFn: authApi.logoutAll,
    onSettled: () => {
      clearAuth();
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });
  const logout = () => {
    void authApi.logout().catch(() => undefined);
    clearAuth();
    queryClient.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className="border-t border-slate-200 px-2 py-2 space-y-1">
      {/* Tenant — лише якщо >1 тенанта */}
      {!collapsed && me.tenants.length > 1 && (
        <Popover
          align="top-left"
          label={
            <span className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-50 rounded-md w-full">
              <span className="truncate text-slate-700">
                {activeTenant?.name ?? t("tenant.no_active")}
              </span>
              <ChevronUp className="h-3.5 w-3.5 ml-auto" />
            </span>
          }
        >
          {(close) =>
            me.tenants.map((tt) => (
              <button
                key={tt.id}
                type="button"
                onClick={() => {
                  onTenantChange(tt.id);
                  close();
                }}
                className={cn(
                  "block w-full text-left px-3 py-2 text-sm hover:bg-slate-50",
                  tt.id === activeTenantId && "bg-brand-navy-50 text-brand-navy-700 font-medium",
                )}
              >
                {tt.name}
              </button>
            ))
          }
        </Popover>
      )}

      {/* User (мова перенесена в Налаштування → Профіль) */}
      <Popover
        align={collapsed ? "right" : "top-left"}
        label={
          <span
            className={cn(
              "flex items-center gap-2 hover:bg-slate-50 rounded-md",
              collapsed ? "justify-center px-2 py-2 w-full" : "px-2 py-1.5 w-full",
            )}
            title={collapsed ? me.full_name : undefined}
          >
            <span className="h-7 w-7 rounded-full bg-brand-navy-500 text-white flex items-center justify-center text-xs font-medium shrink-0">
              {me.full_name.charAt(0).toUpperCase()}
            </span>
            {!collapsed && (
              <>
                <span className="flex flex-col items-start min-w-0">
                  <span className="text-sm font-medium text-slate-700 truncate w-full">
                    {me.full_name}
                  </span>
                  <span className="text-xs text-slate-400 truncate w-full">{me.role}</span>
                </span>
                <ChevronUp className="h-3.5 w-3.5 ml-auto text-slate-400 shrink-0" />
              </>
            )}
          </span>
        }
      >
        {(close) => (
          <>
            <button
              type="button"
              onClick={() => {
                navigate("/settings");
                close();
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 whitespace-nowrap"
            >
              <UserIcon className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{t("user_menu.profile")}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                logoutAll.mutate();
                close();
              }}
              disabled={logoutAll.isPending}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap"
            >
              <MonitorSmartphone className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{t("user_menu.logout_all")}</span>
            </button>
            <div className="border-t border-slate-100 my-1" />
            <button
              type="button"
              onClick={() => {
                logout();
                close();
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 whitespace-nowrap"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{t("user_menu.logout")}</span>
            </button>
          </>
        )}
      </Popover>
    </div>
  );
}

// ─────────────────────────── Popover (bottom-anchored) ───────────────────

/**
 * Popover який можна відкрити **вгору** (для меню в нижній частині sidebar'а).
 * - `align="top-left"`: випливає над тригером, прив'язаний до лівого краю.
 * - `align="right"`: випливає праворуч (для згорнутого sidebar'а).
 */
function Popover({
  label,
  align = "top-left",
  children,
}: {
  label: React.ReactNode;
  align?: "top-left" | "right";
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className="block w-full">
        {label}
      </button>
      {open && (
        <div
          className={cn(
            "absolute min-w-[220px] rounded-md border border-slate-200 bg-white shadow-lg py-1 z-30",
            align === "top-left" && "left-0 bottom-full mb-1",
            align === "right" && "left-full ml-2 bottom-0",
          )}
        >
          {children(close)}
        </div>
      )}
    </div>
  );
}
